import React, { useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import { Table, Badge, Tooltip, Tag, Input, Select, Empty, Button } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import { Link } from 'react-router-dom';
import { startCase } from 'lodash';
import moment from 'moment';
import AvatarS3 from '../../../../shared/images/AvatarS3';
import { urgencyColors } from '../../configs/task.config';
import { projectStatusColors } from '../../configs/project.config';
import { text, space } from '../../configs/designTokens';
import '../../styles/index.scss';

const getRelativeTime = (dateStr) => {
  if (!dateStr) return '—';
  const diff = moment().diff(moment(dateStr), 'days');
  if (diff === 0) return 'Today';
  if (diff === 1) return '1d ago';
  if (diff < 7) return `${diff}d ago`;
  if (diff < 30) return `${Math.floor(diff / 7)}w ago`;
  return moment(dateStr).toLocaleDateString();
};

const { Option } = Select;

const formatEnum = (value) =>
  value
    ? value.split('_').map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()).join(' ')
    : value;

const TeamTasksList = ({ tasks, members, loading, emptyMessage }) => {
  const [searchText, setSearchText] = useState('');
  const [selectedMemberId, setSelectedMemberId] = useState(null);
  const [statusFilter, setStatusFilter] = useState([]);
  const [requestTypeFilter, setRequestTypeFilter] = useState([]);
  const [requestorFilter, setRequestorFilter] = useState([]);
  const [advertiserFilter, setAdvertiserFilter] = useState([]);

  // Derive option lists from the team's tasks so dropdowns only show relevant values
  const statusOptions = useMemo(() => {
    const set = new Set();
    (tasks || []).forEach((t) => { if (t.status) set.add(t.status); });
    return Array.from(set).sort();
  }, [tasks]);

  const requestTypeOptions = useMemo(() => {
    const set = new Set();
    (tasks || []).forEach((t) => { if (t.requestType) set.add(t.requestType); });
    return Array.from(set).sort();
  }, [tasks]);

  const requestorOptions = useMemo(() => {
    const map = new Map();
    (tasks || []).forEach((t) => {
      if (t.createdBy?.id && !map.has(t.createdBy.id)) {
        map.set(t.createdBy.id, t.createdBy);
      }
    });
    return Array.from(map.values()).sort((a, b) =>
      (a.name || a.username || '').localeCompare(b.name || b.username || '')
    );
  }, [tasks]);

  const advertiserOptions = useMemo(() => {
    const map = new Map();
    (tasks || []).forEach((t) => {
      if (t.advertiser?.id && !map.has(t.advertiser.id)) {
        map.set(t.advertiser.id, t.advertiser);
      }
    });
    return Array.from(map.values()).sort((a, b) => (a.name || '').localeCompare(b.name || ''));
  }, [tasks]);

  const filteredTasks = useMemo(() => {
    let result = tasks || [];

    if (searchText) {
      result = result.filter((task) =>
        task.name.toLowerCase().includes(searchText.toLowerCase())
      );
    }
    if (selectedMemberId) {
      result = result.filter((task) =>
        task.assigneeUsers?.some((u) => u.id === selectedMemberId)
      );
    }
    if (statusFilter.length > 0) {
      result = result.filter((task) => statusFilter.includes(task.status));
    }
    if (requestTypeFilter.length > 0) {
      result = result.filter((task) => requestTypeFilter.includes(task.requestType));
    }
    if (requestorFilter.length > 0) {
      result = result.filter((task) => task.createdBy?.id && requestorFilter.includes(task.createdBy.id));
    }
    if (advertiserFilter.length > 0) {
      result = result.filter((task) => task.advertiser?.id && advertiserFilter.includes(task.advertiser.id));
    }

    return result;
  }, [tasks, searchText, selectedMemberId, statusFilter, requestTypeFilter, requestorFilter, advertiserFilter]);

  const hasActiveFilters =
    !!searchText ||
    !!selectedMemberId ||
    statusFilter.length > 0 ||
    requestTypeFilter.length > 0 ||
    requestorFilter.length > 0 ||
    advertiserFilter.length > 0;

  const clearFilters = () => {
    setSearchText('');
    setSelectedMemberId(null);
    setStatusFilter([]);
    setRequestTypeFilter([]);
    setRequestorFilter([]);
    setAdvertiserFilter([]);
  };

  const columns = [
    {
      title: <span className="project-tasks__col-header">Task Name</span>,
      dataIndex: 'name',
      key: 'name',
      width: '25%',
      render: (val, record) => (
        <div className="project-tasks__name-cell">
          <Tooltip title={record.urgency ? `Urgency: ${startCase(record.urgency.toLowerCase())}` : undefined}>
            <Badge
              dot
              color={urgencyColors[record.urgency] || text.muted}
              className="project-tasks__urgency-badge"
            />
          </Tooltip>
          <div className="project-tasks__name-content">
            <Link to={`/tasks/details/${record.id}`} className="project-tasks__name-link">
              {val}
            </Link>
            {record.requestType && (
              <span className="project-tasks__request-type">
                {record.requestType.split('_').map((word) =>
                  word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
                ).join(' ')}
              </span>
            )}
          </div>
        </div>
      ),
    },
    {
      title: <span className="project-tasks__col-header">Due Date</span>,
      dataIndex: 'dueDate',
      key: 'dueDate',
      width: '12%',
      render: (dueDate) => {
        if (!dueDate) return <span className="project-tasks__empty-value">-</span>;
        const due = moment(dueDate);
        const daysLeft = due.diff(moment(), 'days');
        let className = 'project-tasks__due-date';
        if (daysLeft < 0) className += ' project-tasks__due-date--overdue';
        else if (daysLeft <= 3) className += ' project-tasks__due-date--soon';
        return <span className={className}>{due.format('MMM D, YYYY')}</span>;
      },
    },
    {
      title: <span className="project-tasks__col-header">Assignee</span>,
      dataIndex: 'assigneeUsers',
      key: 'assigneeUsers',
      width: '12%',
      render: (assigneeUsers) => {
        if (!assigneeUsers || assigneeUsers.length === 0) {
          return <span className="project-tasks__empty-value">-</span>;
        }
        return (
          <div className="project-tasks__assignees">
            {assigneeUsers.slice(0, 2).map((user) => (
              <Tooltip key={user.id} title={startCase(user.name || user.username)}>
                <div className="project-tasks__assignee-avatar">
                  <AvatarS3
                    imgKey={user.avatar?.key}
                    username={user.name || user.username}
                    avatarProps={{ size: 26 }}
                  />
                </div>
              </Tooltip>
            ))}
            {assigneeUsers.length > 2 && (
              <span className="project-tasks__assignee-more">+{assigneeUsers.length - 2}</span>
            )}
          </div>
        );
      },
    },
    {
      title: <span className="project-tasks__col-header">Requestor</span>,
      dataIndex: 'createdBy',
      key: 'createdBy',
      width: '10%',
      render: (createdBy) => {
        if (!createdBy) {
          return <span className="project-tasks__empty-value">-</span>;
        }
        const name = createdBy.name || createdBy.username || '';
        return (
          <Tooltip title={startCase(name)}>
            <div className="project-tasks__assignee-avatar">
              <AvatarS3
                imgKey={createdBy.avatar?.key}
                username={name}
                avatarProps={{ size: 26 }}
              />
            </div>
          </Tooltip>
        );
      },
    },
    {
      title: <span className="project-tasks__col-header">Created</span>,
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: '10%',
      sorter: (a, b) => new Date(a.createdAt) - new Date(b.createdAt),
      render: (createdAt) => {
        if (!createdAt) return <span className="project-tasks__empty-value">-</span>;
        return (
          <Tooltip title={new Date(createdAt).toLocaleString()}>
            <span>{new Date(createdAt).toLocaleDateString()}</span>
          </Tooltip>
        );
      },
    },
    {
      title: <span className="project-tasks__col-header">Updated</span>,
      dataIndex: 'updatedAt',
      key: 'updatedAt',
      width: '10%',
      sorter: (a, b) => new Date(a.updatedAt) - new Date(b.updatedAt),
      render: (updatedAt) => {
        if (!updatedAt) return <span className="project-tasks__empty-value">-</span>;
        return (
          <Tooltip title={new Date(updatedAt).toLocaleString()}>
            <span>{getRelativeTime(updatedAt)}</span>
          </Tooltip>
        );
      },
    },
    {
      title: <span className="project-tasks__col-header">Status</span>,
      dataIndex: 'status',
      key: 'status',
      width: '12%',
      render: (status) => {
        if (!status) return <span className="project-tasks__empty-value">N/A</span>;
        const formatted = status.split('_').map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()).join(' ');
        return <Tag color={projectStatusColors[status] || 'default'}>{formatted}</Tag>;
      },
    },
  ];

  return (
    <div>
      {/* Filters */}
      <div style={{ display: 'flex', gap: space[3], marginBottom: space[4], flexWrap: 'wrap', alignItems: 'center' }}>
        <Input
          className="team-tasks-search"
          placeholder="Search tasks..."
          prefix={<SearchOutlined style={{ color: text.muted }} />}
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          allowClear
          style={{ width: 240 }}
        />
        <Select
          allowClear
          showSearch
          placeholder="Assignee"
          value={selectedMemberId}
          onChange={(value) => setSelectedMemberId(value || null)}
          style={{ width: 180 }}
          filterOption={(input, option) =>
            option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
          }
        >
          {(members || []).map((member) => (
            <Option key={member.id} value={member.id}>
              {startCase(member.name || member.username)}
            </Option>
          ))}
        </Select>
        <Select
          mode="multiple"
          allowClear
          placeholder="Status"
          value={statusFilter}
          onChange={setStatusFilter}
          style={{ minWidth: 160 }}
          maxTagCount="responsive"
        >
          {statusOptions.map((status) => (
            <Option key={status} value={status}>
              {formatEnum(status)}
            </Option>
          ))}
        </Select>
        <Select
          mode="multiple"
          allowClear
          placeholder="Request type"
          value={requestTypeFilter}
          onChange={setRequestTypeFilter}
          style={{ minWidth: 180 }}
          maxTagCount="responsive"
        >
          {requestTypeOptions.map((type) => (
            <Option key={type} value={type}>
              {formatEnum(type)}
            </Option>
          ))}
        </Select>
        <Select
          mode="multiple"
          allowClear
          showSearch
          placeholder="Requestor"
          value={requestorFilter}
          onChange={setRequestorFilter}
          style={{ minWidth: 180 }}
          maxTagCount="responsive"
          filterOption={(input, option) =>
            option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
          }
        >
          {requestorOptions.map((user) => (
            <Option key={user.id} value={user.id}>
              {startCase(user.name || user.username)}
            </Option>
          ))}
        </Select>
        <Select
          mode="multiple"
          allowClear
          showSearch
          placeholder="Advertiser"
          value={advertiserFilter}
          onChange={setAdvertiserFilter}
          style={{ minWidth: 180 }}
          maxTagCount="responsive"
          filterOption={(input, option) =>
            option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
          }
        >
          {advertiserOptions.map((org) => (
            <Option key={org.id} value={org.id}>
              {org.name}
            </Option>
          ))}
        </Select>
        {hasActiveFilters && (
          <Button onClick={clearFilters} size="small">
            Clear filters
          </Button>
        )}
      </div>

      <Table
        className="task-table"
        dataSource={filteredTasks}
        columns={columns}
        rowKey="id"
        loading={loading}
        pagination={{
          pageSize: 10,
          showSizeChanger: true,
          pageSizeOptions: ['10', '25', '50'],
          showTotal: false,
        }}
        size="middle"
        locale={{
          emptyText: (
            <Empty
              description={selectedMemberId ? 'No tasks for this member' : (emptyMessage || 'No tasks assigned to this team yet')}
            />
          ),
        }}
      />
    </div>
  );
};

TeamTasksList.propTypes = {
  tasks: PropTypes.array,
  members: PropTypes.array,
  loading: PropTypes.bool,
  emptyMessage: PropTypes.string,
};

TeamTasksList.defaultProps = {
  tasks: [],
  members: [],
  loading: false,
  emptyMessage: '',
};

export default TeamTasksList;
